import { mergeDefinition } from './merge';
import { isDecimal, isoDate, compareDecimal } from './values';
import { FIELD_COMPARISONS, SIMPLE_VALUE_TYPES, canHaveValidation, hasValue, isLeaf, type Condition, type FieldDef, type FieldType, type FormDefinition, type JsonValue, type LeafCondition, type RuleName } from './types';

export const LIMITS = { fields: 100, children: 20, depth: 3, leaves: 10, options: 50, textLength: 255 };

export interface DefinitionError {
  field: string;
  code: string;
  rule?: RuleName;
  /** the option whose rule the error is about (rule visibleWhen) */
  option?: string;
}

/** A template definition: every field has origin TEMPLATE. */
export function validateTemplate(definition: FormDefinition): DefinitionError[] {
  const checker = new Checker(definition);
  checker.origins(definition.fields);
  checker.run();
  return checker.result();
}

/** Template + inspection custom fields: placement, key collisions and rules over the merged order. */
export function validateEffective(template: FormDefinition, customFields: FieldDef[]): DefinitionError[] {
  const merged = mergeDefinition(template, customFields);
  const checker = new Checker(merged);
  const topLevel = new Set([...template.fields.map((f) => f.key), ...customFields.map((f) => f.key)]);
  // a custom field and the fields of a custom section or repeating group are all INSPECTION fields
  // only administrators mark (template) fields for charts (D31)
  const customOrigins = (f: FieldDef) => {
    if (f.origin !== 'INSPECTION') checker.error(f.key, 'origin.invalid');
    if (f.chart) checker.error(f.key, 'chart.custom');
    (f.children ?? []).forEach(customOrigins);
  };
  for (const c of customFields) {
    customOrigins(c);
    if (c.placeAfter && !topLevel.has(c.placeAfter)) checker.error(c.key, 'placement.unknown');
  }
  checker.origins(template.fields);
  checker.run();
  return checker.result();
}

class Checker {
  private readonly errors: DefinitionError[] = [];
  private readonly seen = new Set<string>();
  private readonly position = new Map<string, number>();
  private readonly groupOf = new Map<string, string>();
  private readonly byKey = new Map<string, FieldDef>();
  private counter = 0;

  constructor(private readonly def: FormDefinition) {}

  error(field: string, code: string, rule?: RuleName, option?: string) {
    const id = `${field}|${code}|${rule ?? ''}|${option ?? ''}`;
    if (this.seen.has(id)) return;
    this.seen.add(id);
    const e: DefinitionError = { field, code };
    if (rule) e.rule = rule;
    if (option !== undefined) e.option = option;
    this.errors.push(e);
  }

  result(): DefinitionError[] {
    return this.errors;
  }

  origins(fields: FieldDef[]) {
    for (const f of fields) {
      if ((f.origin ?? 'TEMPLATE') !== 'TEMPLATE') this.error(f.key, 'origin.invalid');
      this.origins(f.children ?? []);
    }
  }

  run() {
    this.index(this.def.fields, null);
    if (this.counter > LIMITS.fields) this.error('', 'limit.fields');
    this.check(this.def.fields, null, null);
  }

  private index(fields: FieldDef[], group: string | null) {
    for (const f of fields) {
      this.counter++;
      if (this.byKey.has(f.key)) this.error(f.key, 'key.duplicate');
      else {
        this.byKey.set(f.key, f);
        this.position.set(f.key, this.counter);
        if (group) this.groupOf.set(f.key, group);
      }
      this.index(f.children ?? [], f.type === 'REPEATING_GROUP' ? f.key : group);
    }
  }

  private check(fields: FieldDef[], group: string | null, parent: FieldDef | null) {
    for (const f of fields) {
      if (parent) {
        const nested =
          (parent.type === 'SECTION' && f.type === 'SECTION') ||
          (parent.type === 'REPEATING_GROUP' && (f.type === 'SECTION' || f.type === 'REPEATING_GROUP'));
        if (nested) this.error(f.key, 'children.type');
      }
      if (f.type === 'REPEATING_GROUP' && (f.children ?? []).length > LIMITS.children) this.error(f.key, 'limit.children');
      this.options(f);
      this.table(f);
      this.constraints(f);
      this.chart(f);
      const pos = this.position.get(f.key) ?? Number.MAX_SAFE_INTEGER;
      for (const rule of ['visibleWhen', 'enabledWhen', 'requiredWhen'] as const) this.rule(f, rule, f[rule], group, pos);
      // the validation rule of a field with a simple value; it may also refer to the field itself
      if (f.validWhen && !canHaveValidation(f.type)) this.error(f.key, 'rule.notApplicable', 'validWhen');
      else this.rule(f, 'validWhen', f.validWhen, group, pos, undefined, true);
      // an option rule refers to earlier fields like the rules of its field
      for (const o of f.options ?? []) this.rule(f, 'visibleWhen', o.visibleWhen, group, pos, o.value);
      this.check(f.children ?? [], f.type === 'REPEATING_GROUP' ? f.key : group, f);
    }
  }

  private options(f: FieldDef) {
    if (f.type !== 'SINGLE_CHOICE' && f.type !== 'MULTI_CHOICE') return;
    const options = f.options ?? [];
    if (f.type === 'SINGLE_CHOICE' && options.length < 2) this.error(f.key, 'options.min');
    if (options.length > LIMITS.options) this.error(f.key, 'limit.options');
    const values = options.map((o) => o.value);
    if (new Set(values).size !== values.length) this.error(f.key, 'option.duplicate');
  }

  /**
   * A chart needs numbers (D31): a TABLE always qualifies, a repeating group needs a NUMBER field, a section at least
   * two NUMBER fields directly inside it (parts of one whole). The JSON Schema limits `chart` to these three types.
   */
  private chart(f: FieldDef) {
    if (!f.chart || f.type === 'TABLE') return;
    const numbers = (f.children ?? []).filter((c) => c.type === 'NUMBER').length;
    if (numbers < (f.type === 'SECTION' ? 2 : 1)) this.error(f.key, 'chart.numbers');
  }

  /** The keys of the columns, and of the rows, of a table are unique (they hold the cells of the value). */
  private table(f: FieldDef) {
    if (f.type !== 'TABLE' || !f.table) return;
    const unique = (keys: string[]) => new Set(keys).size === keys.length;
    if (!unique(f.table.columns.map((x) => x.key)) || !unique(f.table.rows.map((x) => x.key))) this.error(f.key, 'table.duplicate');
  }

  private constraints(f: FieldDef) {
    const c = f.constraints ?? {};
    const present = Object.keys(c).filter((k) => (c as Record<string, unknown>)[k] !== undefined);
    const allowed: Record<FieldType, string[]> = {
      TEXT: ['maxLength', 'format'],
      TEXTAREA: ['maxLength', 'rows'],
      RICH_TEXT: ['maxLength'],
      TABLE: ['maxLength'],
      NUMBER: ['min', 'max', 'decimals'],
      DATE: ['min', 'max', 'relative'],
      SINGLE_CHOICE: ['display'],
      MULTI_CHOICE: ['minSelected', 'maxSelected', 'display', 'rows'],
      REPEATING_GROUP: ['minRows', 'maxRows'],
      INFO: ['variant'],
      AUTOCOMPLETE: ['source'],
      YES_NO: [],
      TEAM_MEMBER: [],
      SWITCH: [],
      SECTION: [],
    };
    if (!present.every((k) => allowed[f.type].includes(k))) return this.error(f.key, 'constraint.notApplicable');
    // the lines shown apply to a list only (not to checkboxes)
    if (f.type === 'MULTI_CHOICE' && c.rows !== undefined && c.display !== 'select') return this.error(f.key, 'constraint.notApplicable');
    switch (f.type) {
      case 'TEXT':
      case 'TABLE':
        if (c.maxLength !== undefined && c.maxLength > LIMITS.textLength) this.error(f.key, 'constraint.range');
        return;
      case 'NUMBER':
        if ((c.min !== undefined && !isDecimal(c.min)) || (c.max !== undefined && !isDecimal(c.max))) this.error(f.key, 'constraint.invalid');
        else if (c.min !== undefined && c.max !== undefined && compareDecimal(c.min, c.max) > 0) this.error(f.key, 'constraint.range');
        return;
      case 'DATE':
        if ((c.min !== undefined && !isoDate(c.min)) || (c.max !== undefined && !isoDate(c.max))) this.error(f.key, 'constraint.invalid');
        else if (c.min !== undefined && c.max !== undefined && c.min > c.max) this.error(f.key, 'constraint.range');
        return;
      case 'SINGLE_CHOICE':
        if (c.display !== undefined && c.display !== 'radios' && c.display !== 'select') this.error(f.key, 'constraint.invalid');
        return;
      case 'MULTI_CHOICE': {
        const n = (f.options ?? []).length;
        if (c.display !== undefined && c.display !== 'checkboxes' && c.display !== 'select') this.error(f.key, 'constraint.invalid');
        else if (
          (c.minSelected !== undefined && c.maxSelected !== undefined && c.minSelected > c.maxSelected) ||
          (c.minSelected !== undefined && c.minSelected > n) ||
          (c.maxSelected !== undefined && c.maxSelected > n)
        )
          this.error(f.key, 'constraint.range');
        return;
      }
      case 'REPEATING_GROUP':
        if (c.minRows !== undefined && c.maxRows !== undefined && c.minRows > c.maxRows) this.error(f.key, 'constraint.range');
        return;
      default:
    }
  }

  /** `self`: the rule may refer to its own field (validWhen). */
  private rule(f: FieldDef, rule: RuleName, c: Condition | undefined, group: string | null, pos: number, option?: string, self = false) {
    if (!c) return;
    if (conditionDepth(c) > LIMITS.depth) this.error(f.key, 'limit.depth', rule, option);
    if (leafCount(c) > LIMITS.leaves) this.error(f.key, 'limit.leaves', rule, option);
    for (const leaf of leaves(c)) {
      const code = this.leafError(leaf, group, pos, self);
      if (code) this.error(f.key, code, rule, option);
    }
  }

  /** The first problem of a leaf condition (same order of checks as the backend), or null. */
  leafError(leaf: LeafCondition, group: string | null, pos: number, self = false): string | null {
    const fieldError = this.refError(leaf.field, group, pos, self);
    if (fieldError) return fieldError;
    const ref = this.byKey.get(targetKey(leaf.field))!;
    if (!operatorFits(leaf.op, ref.type)) return 'op.type';
    if (leaf.valueField === undefined) return valueError(leaf, ref);
    // compared with the value of another field: of the same type, by an operator that compares values
    if (!FIELD_COMPARISONS.includes(leaf.op)) return 'value.field';
    const otherError = this.refError(leaf.valueField, group, pos, self);
    if (otherError) return otherError;
    return this.byKey.get(targetKey(leaf.valueField))!.type === ref.type ? null : 'value.field';
  }

  /** A field a rule may refer to: it exists, is in reach (same row or outside groups), earlier and has a value. */
  private refError(reference: string, group: string | null, pos: number, self: boolean): string | null {
    const rowRef = reference.startsWith('row.');
    const target = targetKey(reference);
    const ref = this.byKey.get(target);
    if (!ref) return 'ref.unknown';
    const targetGroup = this.groupOf.get(target) ?? null;
    if (rowRef) {
      if (!group || group !== targetGroup) return 'ref.scope';
    } else if (targetGroup) return 'ref.scope';
    const targetPos = this.position.get(target) ?? 0;
    if (targetPos > pos || (targetPos === pos && !self)) return 'ref.forward';
    if (!hasValue(ref.type)) return 'ref.noValue';
    return null;
  }
}

function targetKey(reference: string): string {
  return reference.startsWith('row.') ? reference.slice(4) : reference;
}

export function conditionDepth(c: Condition): number {
  if ('all' in c) return 1 + Math.max(0, ...c.all.map(conditionDepth));
  if ('any' in c) return 1 + Math.max(0, ...c.any.map(conditionDepth));
  if ('not' in c) return 1 + conditionDepth(c.not);
  return 0;
}

export function leaves(c: Condition): LeafCondition[] {
  if ('all' in c) return c.all.flatMap(leaves);
  if ('any' in c) return c.any.flatMap(leaves);
  if ('not' in c) return leaves(c.not);
  return isLeaf(c) ? [c] : [];
}

export function leafCount(c: Condition): number {
  return leaves(c).length;
}

/** Operators by the type of the referenced field (§7.6). */
export function operatorsFor(type: FieldType): string[] {
  return ['eq', 'neq', 'in', 'notIn', 'includes', 'excludes', 'empty', 'notEmpty', 'gt', 'gte', 'lt', 'lte'].filter((op) => operatorFits(op, type));
}

export function operatorFits(op: string, type: FieldType): boolean {
  switch (op) {
    case 'eq':
    case 'neq':
      return SIMPLE_VALUE_TYPES.includes(type) && type !== 'AUTOCOMPLETE';
    case 'in':
    case 'notIn':
      return type === 'SINGLE_CHOICE' || type === 'TEAM_MEMBER';
    case 'includes':
    case 'excludes':
      return type === 'MULTI_CHOICE';
    case 'empty':
    case 'notEmpty':
      return hasValue(type);
    case 'gt':
    case 'gte':
    case 'lt':
    case 'lte':
      return type === 'NUMBER' || type === 'DATE';
    default:
      return false;
  }
}

function valueError(leaf: LeafCondition, ref: FieldDef): string | null {
  const { op } = leaf;
  if (op === 'empty' || op === 'notEmpty') return null;
  const v = leaf.value;
  if (v === undefined || v === null) return 'value.missing';
  switch (op) {
    case 'eq':
    case 'neq':
      return scalarError(ref, v, true);
    case 'in':
    case 'notIn': {
      if (!Array.isArray(v) || v.length === 0) return 'value.type';
      for (const x of v) {
        const e = scalarError(ref, x, false);
        if (e) return e;
      }
      return null;
    }
    case 'includes':
    case 'excludes':
      if (typeof v !== 'string') return 'value.type';
      return (ref.options ?? []).some((o) => o.value === v) ? null : 'value.option';
    default:
      return scalarError(ref, v, false);
  }
}

function scalarError(ref: FieldDef, v: JsonValue, multiArray: boolean): string | null {
  const isOption = (x: JsonValue) => (ref.options ?? []).some((o) => o.value === x);
  switch (ref.type) {
    case 'YES_NO':
    case 'SWITCH':
      return typeof v === 'boolean' ? null : 'value.type';
    case 'NUMBER':
      return isDecimal(v) ? null : 'value.type';
    case 'DATE':
      return isoDate(v) ? null : 'value.type';
    case 'SINGLE_CHOICE':
      if (typeof v !== 'string') return 'value.type';
      return isOption(v) ? null : 'value.option';
    case 'MULTI_CHOICE':
      if (!multiArray || !Array.isArray(v)) return 'value.type';
      for (const x of v) {
        if (typeof x !== 'string') return 'value.type';
        if (!isOption(x)) return 'value.option';
      }
      return null;
    default:
      return typeof v === 'string' ? null : 'value.type';
  }
}
